import { createSlice, createSelector } from "@reduxjs/toolkit";
import { apiCallBegan } from "./api";
import config from "../../config.json";
import { createRequestState, requestFailed, requestStarted, requestSucceeded } from "./requestState";

const slice = createSlice({
  name: "documents",
  initialState: {
    list: [],
    loading: false,
    lastFetch: null,
    requests: {
      documents: createRequestState(),
      reqDocuments: createRequestState(),
      esignDocuments: createRequestState(),
    },
    sendStatus: false,
    esignSendStatus: false,
    deleteStatus: false,
    purgeStatus: false,
    rollbackStatus: false,
    docDownloadData: null,
    reqDocuments: [],
    esignDocuments: [],
    reqDocAddedStatus: false,
    multipleReqDocAddedStatus: false,
    reqDocSendStatus: false,
    uploadedDocumentId: null,
    documentById: null,
    signedDocument: null,
    localDocUploadStatus: null,
    localGeneratedDocUploadStatus: null,
    localDocUploadSasUrl: null,
    localGeneratedDocUploadSasUrl: null,
    otherlocalDocUploadSasUrl: null,
    wfPhases: [],
    ddrmsViewDocs: null,
    reqDocDeleteStatus: null,
    receivedDocStatus: null,
    receivedDocDeleteStatus: null,
    esignPrepToken: null,
    esignUploadStatus: null,
    docEsignLinks: {},
  },
  reducers: {
    documentRequestStarted: (documents, action) => {
      requestStarted(documents.requests, action.payload);
    },
    documentRequestFailed: (documents, action) => {
      requestFailed(documents.requests, action.payload?.key, action.payload?.error || action.payload);
    },
    documentReceived: (documents, action) => {
      requestSucceeded(documents.requests, "documents");
      documents.list = action.payload;
      documents.reqDocAddedStatus = false;
      documents.multipleReqDocAddedStatus = false;
      documents.sendStatus = false;
      documents.esignSendStatus = false;
      documents.deleteStatus = false;
      documents.purgeStatus = false;
      documents.rollbackStatus = false;
      documents.reqDocDeleteStatus = false;
      documents.localDocUploadStatus = null;
      documents.localDocUploadSasUrl = null;
      documents.esignPrepToken = null;
      documents.esignUploadStatus = null;
    },
    documentSent: (documents, action) => {
      documents.sendStatus = true;
    },
    documentDeleted: (documents, action) => {
      documents.deleteStatus = true;
    },
    documentReqDeleted: (documents, action) => {
      documents.reqDocDeleteStatus = true;
    },
    documentPurged: (documents, action) => {
      documents.purgeStatus = true;
    },
    documentRollBacked: (documents, action) => {
      documents.rollbackStatus = true;
    },
    esignDocumentSent: (documents, action) => {
      documents.esignSendStatus = true;
    },
    reqDocumentReceived: (documents, action) => {
      documents.list = action.payload;
    },
    documentDownloaded: (documents, action) => {
      documents.docDownloadData = action.payload;
    },
    reqDocumentAdded: (documents, action) => {
      documents.reqDocAddedStatus = action.payload;
    },
    multipleReqDocumentAdded: (documents, action) => {
      documents.multipleReqDocAddedStatus = action.payload;
    },
    reqDocumentSent: (documents, action) => {
      documents.reqDocSendStatus = action.payload;
    },
    reqDocumentsReceived: (documents, action) => {
      requestSucceeded(documents.requests, "reqDocuments");
      documents.reqDocuments = action.payload;
      documents.reqDocAddedStatus = false;
      documents.reqDocSendStatus = false;
      documents.reqDocDeleteStatus = false;
    },
    esignDocumentsReceived: (documents, action) => {
      requestSucceeded(documents.requests, "esignDocuments");
      documents.esignDocuments = action.payload;
      documents.esignSendStatus = false;
    },
    signedDocumentReceived: (documents, action) => {
      documents.signedDocument = action.payload;
    },
    documentUploaded: (documents, action) => {
      const payload = action.payload;
      if (!payload) {
        documents.uploadedDocumentId = null;
        documents.otherlocalDocUploadSasUrl = null;
        return;
      }

      documents.uploadedDocumentId = payload.uploadId ?? payload;
      documents.otherlocalDocUploadSasUrl = payload.sasUrls ?? null;
    },
    documentByIdReceived: (documents, action) => {
      documents.documentById = action.payload;
    },
    localDocumentUploaded: (documents, action) => {
      const payload = action.payload;
      if (!payload) {
        documents.localDocUploadStatus = null;
        documents.localDocUploadSasUrl = null;
        return;
      }

      documents.localDocUploadStatus = payload.uploadId ?? payload;
      documents.localDocUploadSasUrl = payload.sasUrls ?? null;
    },
    localGeneratedDocumentUploaded: (documents, action) => {
      const payload = action.payload;
      if (!payload) {
        documents.localGeneratedDocUploadStatus = null;
        documents.localGeneratedDocUploadSasUrl = null;
        return;
      }

      documents.localGeneratedDocUploadStatus = payload.uploadId ?? payload;
      documents.localGeneratedDocUploadSasUrl = payload.sasUrls ?? null;
    },
    wfPhasesReceived: (dashboard, action) => {
      dashboard.wfPhases = action.payload;
    },
    ddrmsViewFileDocsReceived: (documents, action) => {
      documents.ddrmsViewDocs = action.payload;
    },
    receviedDocStatusUpdated: (documents, action) => {
      documents.receivedDocStatus = action.payload;
    },
    receviedDocDeleted: (documents, action) => {
      documents.receivedDocDeleteStatus = action.payload;
    },
    esignUploadSuccess: (documents, action) => {
      documents.esignPrepToken = action.payload.preparationToken;
      documents.esignUploadStatus = "success";
    },
    esignUploadFailed: (documents, action) => {
      documents.esignPrepToken = null;
      documents.esignUploadStatus = "failed";
    },
    esignUploadReset: (documents, action) => {
      documents.esignPrepToken = null;
      documents.esignUploadStatus = null;
    },
    docEsignLinksReceived: (documents, action) => {
      if (action.payload?.docId) {
        documents.docEsignLinks[action.payload.docId] = action.payload;
      }
    },
  },
});

export const {
  documentRequestStarted,
  documentRequestFailed,
  documentReceived,
  documentSent,
  reqDocumentReceived,
  esignDocumentSent,
  documentDeleted,
  documentPurged,
  documentRollBacked,
  documentDownloaded,
  reqDocumentsReceived,
  reqDocumentAdded,
  multipleReqDocumentAdded,
  documentUploaded,
  reqDocumentSent,
  documentByIdReceived,
  esignDocumentsReceived,
  signedDocumentReceived,
  localDocumentUploaded,
  localGeneratedDocumentUploaded,
  wfPhasesReceived,
  ddrmsViewFileDocsReceived,
  receviedDocStatusUpdated,
  receviedDocDeleted,
  documentReqDeleted,
  esignUploadSuccess,
  esignUploadFailed,
  esignUploadReset,
  docEsignLinksReceived,
} = slice.actions;
export default slice.reducer;

export const loadDocuments = (id) =>
  apiCallBegan({
    url: config.GetApplicationDocuments + id,
    onStart: { type: documentRequestStarted.type, payload: "documents" },
    onSuccess: documentReceived.type,
    onError: { type: documentRequestFailed.type, payload: { key: "documents" } },
  });

export const loadDocumentById = (id) =>
  apiCallBegan({
    url: config.GetDocumentById + id,
    onSuccess: documentByIdReceived.type,
  });

export const sendDocuments = (data) =>
  apiCallBegan({
    url: config.UpdateDocSentStatus,
    method: "post",
    data: data,
    onSuccess: documentSent.type,
  });

export const sendeSignDocuments = (data) =>
  apiCallBegan({
    url: config.SendeSignDocument,
    method: "post",
    data: data,
    onSuccess: esignDocumentSent.type,
  });

export const uploadToEsign = (data) =>
  apiCallBegan({
    url: config.UploadToESign,
    method: "post",
    data: data,
    onSuccess: esignUploadSuccess.type,
    onError: esignUploadFailed.type,
  });

export const sendReqDocuments = (id) =>
  apiCallBegan({
    url: config.SendRequiredDocument + id,
    onSuccess: reqDocumentSent.type,
  });

export const deleteDocument = (id) =>
  apiCallBegan({
    url: config.DeleteUploadedDocument + id,
    onSuccess: documentDeleted.type,
  });

export const updateReceivedDocStatus = (id, status, type) =>
  apiCallBegan({
    url:
      config.UpdateApplicantDocStatus +
      id +
      "&status=" +
      status +
      "&type=" +
      type,
    onSuccess: receviedDocStatusUpdated.type,
  });

export const deleteReceivedDocument = (id, type) =>
  apiCallBegan({
    url: config.DeleteApplicantDoc + id + "&type=" + type,
    method: "delete",
    onSuccess: receviedDocDeleted.type,
  });

export const deleteReqDocument = (id) =>
  apiCallBegan({
    url: config.DeleteRequiredDocument + id,
    onSuccess: documentReqDeleted.type,
  });

export const purgeDocument = (id) =>
  apiCallBegan({
    url: config.PurgeUploadedDocument + id,
    onSuccess: documentPurged.type,
  });

export const rollbackDocument = (id) =>
  apiCallBegan({
    url: config.RollbackUploadedDocument + id,
    onSuccess: documentRollBacked.type,
  });

export const loadReqDocuments = (id) =>
  apiCallBegan({
    url: config.GetRequiredDocument + id,
    onStart: { type: documentRequestStarted.type, payload: "reqDocuments" },
    onSuccess: reqDocumentsReceived.type,
    onError: { type: documentRequestFailed.type, payload: { key: "reqDocuments" } },
  });

export const loadEsignDocuments = (id) =>
  apiCallBegan({
    url: config.GetDocusingDocuments + id,
    onStart: { type: documentRequestStarted.type, payload: "esignDocuments" },
    onSuccess: esignDocumentsReceived.type,
    onError: { type: documentRequestFailed.type, payload: { key: "esignDocuments" } },
  });

export const loadSignedDocument = (id, type) =>
  apiCallBegan({
    url: config.DownloadSignedDocument + id + "&type=" + type,
    //responseType: "blob",
    onSuccess: signedDocumentReceived.type,
  });

export const loadDocEsignLinks = (docId) =>
  apiCallBegan({
    url: config.GetDocumentESignLinks + docId,
    onSuccess: docEsignLinksReceived.type,
  });

export const addReqDocuments = (data) =>
  apiCallBegan({
    url: config.AddRequiredDocument,
    method: "post",
    data: data,
    onSuccess: reqDocumentAdded.type,
  });
export const addMultipleReqDocuments = (data) =>
  apiCallBegan({
    url: config.AddMultipleRequiredDocument,
    method: "post",
    data: data,
    onSuccess: multipleReqDocumentAdded.type,
  });

export const downloadDocument = (id) =>
  apiCallBegan({
    url: config.DownloadDocument + id,
    //responseType: "blob",
    onSuccess: documentDownloaded.type,
  });

export const uploadDocument = (data) =>
  apiCallBegan({
    url: config.ApplicantUploadDocument,
    method: "post",
    data: data,
    onSuccess: documentUploaded.type,
  });

export const loadWfPhasesDocs = (name) =>
  apiCallBegan({
    url: config.GetCodemanager + name,
    onSuccess: wfPhasesReceived.type,
  });

export const uploadLocalDocument = (data) =>
  apiCallBegan({
    url: config.ApplicantUploadDocument,
    method: "post",
    data: data,
    onSuccess: localDocumentUploaded.type,
  });

export const uploadGeneratedLocalDocument = (data) =>
  apiCallBegan({
    url: config.ApplicantUploadDocument,
    method: "post",
    data: data,
    onSuccess: localGeneratedDocumentUploaded.type,
  });

export const loadDdrmsViewDocs = (id) =>
  apiCallBegan({
    url: config.GetDdrmsViewDocs + id,
    //responseType: "blob",
    onSuccess: ddrmsViewFileDocsReceived.type,
  });

export const getDocuments = createSelector(
  (state) => state.entities.documents,
  (documents) => documents.list,
);

export const getDocumentsLoading = createSelector(
  (state) => state.entities.documents,
  (documents) => documents.requests.documents?.loading || false,
);

// True while any of the three document lists a review panel needs is in flight.
export const getAnyDocumentsLoading = createSelector(
  (state) => state.entities.documents,
  (documents) =>
    Boolean(
      documents.requests.documents?.loading ||
      documents.requests.reqDocuments?.loading ||
      documents.requests.esignDocuments?.loading,
    ),
);

export const getDocumentsSendStatus = createSelector(
  (state) => state.entities.documents,
  (documents) => documents.sendStatus,
);
export const getesignDocumentsSendStatus = createSelector(
  (state) => state.entities.documents,
  (documents) => documents.esignSendStatus,
);

export const getReqDocuments = createSelector(
  (state) => state.entities.documents,
  (documents) => documents.reqDocuments,
);

export const getEsignDocuments = createSelector(
  (state) => state.entities.documents,
  (documents) => documents.esignDocuments,
);

export const getSignedDocumentData = createSelector(
  (state) => state.entities.documents,
  (documents) => documents.signedDocument,
);

export const getReqDocAddedStatus = createSelector(
  (state) => state.entities.documents,
  (documents) => documents.reqDocAddedStatus,
);
export const getMultipleReqDocAddedStatus = createSelector(
  (state) => state.entities.documents,
  (documents) => documents.multipleReqDocAddedStatus,
);
export const getReqDocSendStatus = createSelector(
  (state) => state.entities.documents,
  (documents) => documents.reqDocSendStatus,
);

export const getDocumentDeleteStatus = createSelector(
  (state) => state.entities.documents,
  (documents) => documents.deleteStatus,
);

export const getReqDocDeleteStatus = createSelector(
  (state) => state.entities.documents,
  (documents) => documents.reqDocDeleteStatus,
);

export const getDocumentPurgeStatus = createSelector(
  (state) => state.entities.documents,
  (documents) => documents.purgeStatus,
);

export const getDocumentRollbackStatus = createSelector(
  (state) => state.entities.documents,
  (documents) => documents.rollbackStatus,
);

export const getDownloadedDocumentData = createSelector(
  (state) => state.entities.documents,
  (documents) => documents.docDownloadData,
);

export const getUploadDocumentId = createSelector(
  (state) => state.entities.documents,
  (documents) => documents.uploadedDocumentId,
);

export const getLocalDocUploadStatus = createSelector(
  (state) => state.entities.documents,
  (documents) => documents.localDocUploadStatus,
);

export const getLocalDocUploadSasUrl = createSelector(
  (state) => state.entities.documents,
  (documents) => documents.localDocUploadSasUrl,
);

export const getLocalGeneratedDocUploadSasUrl = createSelector(
  (state) => state.entities.documents,
  (documents) => documents.localGeneratedDocUploadSasUrl,
);

export const getDocumentById = createSelector(
  (state) => state.entities.documents,
  (documents) => documents.documentById,
);

export const getWfPhasesDoc = createSelector(
  (state) => state.entities.documents,
  (documents) => documents.wfPhases,
);

export const getDdrmsViewDocs = createSelector(
  (state) => state.entities.documents,
  (documents) => documents.ddrmsViewDocs,
);

export const getReceivedDocUpdateStatus = createSelector(
  (state) => state.entities.documents,
  (documents) => documents.receivedDocStatus,
);

export const getReceivedDocDeleteStatus = createSelector(
  (state) => state.entities.documents,
  (documents) => documents.receivedDocDeleteStatus,
);

export const getOtherLocalDocUploadSasUrl = createSelector(
  (state) => state.entities.documents,
  (documents) => documents.otherlocalDocUploadSasUrl,
);

export const getEsignPrepToken = createSelector(
  (state) => state.entities.documents,
  (documents) => documents.esignPrepToken,
);

export const getEsignUploadStatus = createSelector(
  (state) => state.entities.documents,
  (documents) => documents.esignUploadStatus,
);

export const getDocEsignLinks = createSelector(
  (state) => state.entities.documents,
  (documents) => documents.docEsignLinks || {}
);
